import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import { CustomTooltip } from '@components/shared/CustomTooltip';
import { Checkbox, Stack, Typography } from '@mui/material';
import { alpha, Box } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { getStatusColor } from '@utils/colorUtils';
import formatString from '@utils/formatString';
import { useMemo } from 'react';
import { CustomNoRowsOverlay } from '../shared/CustomNoRowsOverlay';
import Spinner from '../spinner/Spinner';

export const CustomTableFilter = ({
  data,
  columns,
  isPending,
  setPaginationModel,
  paginationModel,
  filterToolbar,
  noRowsTitle,
  noRowsText,
  type,
  rowSelectProps,
  checkboxSelection,
  onRowSelectionModelChange,
  isRowSelected,
  noRowsIconType,
  rowSelectionModel,
  enableServerSorting,
  onGoBack,
  density = 'default',
}) => {
  const hasStudies = data && data?.data?.length > 0;
  const hasParticipants = data && data?.data?.length > 0;
  const isDense = density === 'compact';

  const paginationState = useMemo(
    () => ({
      page: paginationModel.offset / paginationModel.limit || 0,
      pageSize: paginationModel.limit || 5,
    }),
    [paginationModel],
  );
  const statusTooltips = {
    active:
      'Participants are actively responding to the study. Editing may impact ongoing responses.',
    draft:
      ' The study has been created but is not yet set up or finalized. You can still make changes.',
    created: "The study is set up but hasn't started collecting responses yet.",
    inactive:
      'The study has ended, or responses are no longer being collected. Participants cannot submit responses.',
    archived: 'The study is fully completed and locked. No further actions can be taken.',
    default: 'Status unknown.',
  };

  return (
    <>
      <Box px={{ xs: 2, md: 3 }} pb={5}>
        <Stack spacing={1}>
          {filterToolbar && filterToolbar}
          <Box
            sx={{
              '& .super-app-theme--header': {
                fontWeight: 600,
                backgroundColor: '#F2F2F2',
                padding: '0 20px !important',
              },
              '& .MuiDataGrid-cell': {
                padding: isDense ? '10px 12px!important' : '16px  12px!important',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
              '& .MuiDataGrid-withBorderColor ': {
                borderColor: hasStudies ? '#E8E8E8 !important' : 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                display: hasStudies ? 'flex' : 'none',
                position: 'relative',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '0px',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'space-between',
              },
              '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
                color: '#7d5ae8', // your desired color
              },
              '& .stickyColumn': {
                position: 'sticky',
                right: 0,
                backgroundColor: '#fff',
                zIndex: 2,
                borderLeft: '1px solid #ccc',
              },
              '& .stickyHeader': {
                position: 'sticky !important',
                right: '0px',
                backgroundColor: '#fff',
                zIndex: 2,
                borderLeft: '1px solid #ccc',
              },
            }}
          >
            {isPending ? (
              <Spinner />
            ) : (
              <>
                {rowSelectProps && isRowSelected && rowSelectProps}
                <DataGrid
                  loading={isPending}
                  rows={data?.data || []}
                  pinnedColumns={{ right: ['action'] }}
                  rowHeight={isDense ? 52 : 65}
                  columnHeaderHeight={isDense ? 40 : 45}
                  columns={
                    hasStudies
                      ? columns.map((column) => {
                          if (column.field === 'status') {
                            return {
                              ...column,
                              renderCell: (params) => {
                                const { status } = params.row;
                                const tooltipText =
                                  statusTooltips[status] || statusTooltips.default;

                                const statusColor = getStatusColor(status);
                                return (
                                  <CustomTooltip
                                    title={`${formatString(status)}: ${tooltipText}`}
                                    arrow
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          backgroundColor: statusColor,
                                          boxShadow: `0 0 6px ${alpha(statusColor, 0.5)}`,
                                          animation:
                                            status === 'active' ? 'pulse 2s infinite' : 'none',
                                          '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.6 },
                                          },
                                        }}
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {formatString(status)}
                                      </Typography>
                                    </Box>
                                  </CustomTooltip>
                                );
                              },
                            };
                          }
                          return column;
                        })
                      : []
                  }
                  rowCount={data?.total || 0}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 20, 50, 100]}
                  paginationModel={paginationState}
                  getRowId={(row) => row.id} //  the actual ID from ai participants data
                  sortingMode={enableServerSorting ? 'server' : 'client'}
                  filterMode="server"
                  paginationMode="server"
                  slots={{
                    noRowsOverlay: () => (
                      <CustomNoRowsOverlay
                        title={noRowsTitle}
                        text={noRowsText}
                        noRowsIconType={noRowsIconType || 'studies'}
                        type={type}
                        onGoBack={onGoBack}
                      />
                    ),
                    baseCheckbox: (props) => (
                      <Checkbox checkedIcon={<CheckedIcon />} icon={<UnCheckedIcon />} {...props} />
                    ),
                  }}
                  checkboxSelection={hasParticipants && checkboxSelection}
                  onRowSelectionModelChange={onRowSelectionModelChange}
                  rowSelectionModel={rowSelectionModel}
                  onSortModelChange={(model) =>
                    setPaginationModel({
                      ...paginationModel,
                      sort_order: model[0]?.sort,
                      sort_by: model[0]?.field,
                    })
                  }
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      display: isRowSelected ? 'none' : 'flex',
                      backgroundColor: 'rgb(243 244 246)',
                    },
                    height: data?.data?.length ? 'auto' : '600px',
                    overflow: 'hidden',
                    border: '1px solid #E8E8E8',
                    backgroundColor: 'background.default',
                  }}
                  hideFooter={!hasStudies}
                  onPaginationModelChange={(model) => {
                    setPaginationModel({
                      ...paginationModel,
                      limit: model.pageSize,
                      offset: model.page * model.pageSize,
                    });
                  }}
                />
              </>
            )}
          </Box>
        </Stack>
      </Box>
    </>
  );
};
